import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [activeLink, setActiveLink] = useState("Home");

  const navLinks = [
    { name: "Home", path: "/" },
    {
      name: "Courses",
      path: "/courses",
      dropdown: [
        { name: "Screen-Free Coding & Computation", path: "/courses/screen-free" },
        { name: "Online Coding & Computation", path: "/courses/online" },
        { name: "Scratch Programming", path: "/courses/scratch" },
        { name: "Bot Programming", path: "/courses/bot-programming" },
        { name: "Home & Online Coaching", path: "/courses/home-coaching" },
      ],
    },
    { name: "Gallery", path: "/gallery" },
    { name: "About Us", path: "/about" },
    { name: "Inquiry", path: "/contact" },
  ];

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === "/") setActiveLink("Home");
    else if (currentPath === "/about") setActiveLink("About Us");
    else if (currentPath === "/gallery") setActiveLink("Gallery");
    else if (currentPath === "/contact") setActiveLink("Contact Us");
    else if (currentPath.startsWith("/courses")) setActiveLink("Courses");
    else setActiveLink("");
  }, [location]);

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between py-2">
          <div className="shrink-0 flex items-center">
            <div className="flex flex-col">
              <Link to="/">
                <img className="h-15 w-auto" src="/code-riite-logo-main.png" alt="CodeRiite" />
              </Link>
            </div>
          </div>
          <div className="hidden md:flex space-x-8 items-center">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.path ? (
                  <Link
                    to={link.path}
                    className={`px-3 py-2 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-0.5 flex items-center gap-1 ${
                      activeLink === link.name
                        ? "text-primary border-b-2 border-primary"
                        : "text-gray-600 hover:text-primary"
                    }`}
                  >
                    {link.name}
                    {link.dropdown && <ChevronDown className="w-4 h-4" />}
                  </Link>
                ) : (
                  <span
                    className={`px-3 py-2 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-0.5 flex items-center gap-1 cursor-default ${
                      activeLink === link.name
                        ? "text-primary border-b-2 border-primary"
                        : "text-gray-600 hover:text-primary"
                    }`}
                  >
                    {link.name}
                    {link.dropdown && <ChevronDown className="w-4 h-4" />}
                  </span>
                )}

                {link.dropdown && (
                  <div className="absolute left-0 mt-3 w-64 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left z-50">
                    <div className="py-1">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-primary cursor-pointer"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
           
          </div>
          {/* Mobile menu button */}
          <div className="-mr-2 flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-primary focus:outline-none"
            >
              <span className="sr-only">Open main menu</span>
              <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                <path
                  className={isOpen ? "hidden" : "inline-flex"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
                <path
                  className={isOpen ? "inline-flex" : "hidden"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`${isOpen ? "block" : "hidden"} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navLinks.map((link) => (
            <div key={link.name}>
              {link.path ? (
                <Link
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    activeLink === link.name
                      ? "text-primary bg-orange-50"
                      : "text-gray-700 hover:text-primary hover:bg-gray-50"
                  }`}
                >
                  {link.name}
                </Link>
              ) : (
                <span
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    activeLink === link.name
                      ? "text-primary bg-orange-50"
                      : "text-gray-700 hover:text-primary hover:bg-gray-50"
                  }`}
                >
                  {link.name}
                </span>
              )}
              {link.dropdown && (
                <div className="pl-4 space-y-1">
                  {link.dropdown.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setIsOpen(false)}
                      className="block px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a
            href="https://braindevelopment.tickleright.com/signin.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-700 hover:text-primary block px-3 py-2 rounded-md text-base font-medium hover:bg-gray-50"
          >
            LOGIN
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
